// src/components/AnalysisAssistant.jsx
import React, { useState, useRef, useEffect } from 'react';
import { readDesignContext } from '../utils/designStore';
import { getParams as getSunParams, getResults as getSunResults } from '../utils/sunPathStore';
import { getParams as getWindParams, getResults as getWindResults } from '../utils/windStore';
import {
  getParams as getTempParams,
  getResults as getTempResults,
  getClimate as getTempClimate,
} from '../utils/temperatureStore';

/**
 * AnalysisAssistant — one chat strip shared by every analysis in the bottom
 * drawer (compliance, climate, water, electricity).
 *
 * It is deliberately NOT a general chatbot. Every answer is grounded:
 *   compliance -> verbatim regulation passages, cited to rulebook + page
 *   climate    -> the Sun/Wind/Temperature numbers THIS project computed,
 *                 read straight out of the stores and sent as context
 *   water /
 *   electricity-> not built, so it says so instead of inventing figures
 *
 * The open panel is passed as `activeTab` and used as a routing prior, so
 * "is this right?" means the analysis the architect is actually looking at.
 */

const T = {
  surface: 'var(--surface-2,#1a2234)',
  surfaceHi: 'var(--surface-3,#212a3e)',
  border: 'var(--border,#3a4456)',
  text: 'var(--text,#e6ebf5)',
  textDim: 'var(--text-dim,#9aa7c0)',
  accent: 'var(--accent,#5b9dff)',
  warn: 'var(--warn,#f0b429)',
  err: 'var(--err,#f0616d)',
};

const DOMAIN_LABEL = {
  compliance: 'Compliance · regulations',
  climate: 'Climate · computed',
  water: 'Water',
  electricity: 'Electricity',
};

/** Snapshot whatever the climate tools have computed for this project. */
function readClimateContext(facts) {
  const sunResults = getSunResults();
  const windResults = getWindResults();
  const tempResults = getTempResults();
  const tempClimate = getTempClimate();
  const any = sunResults || windResults || tempResults || tempClimate;
  if (!any) return null;
  return {
    facts: facts || null,
    sun: sunResults ? { params: getSunParams(), results: sunResults } : null,
    wind: windResults ? { params: getWindParams(), results: windResults } : null,
    temperature:
      tempResults || tempClimate
        ? { params: getTempParams(), results: tempResults, climate: tempClimate }
        : null,
  };
}

/**
 * What the app already knows about the site, without anything being built.
 *
 * The assistant used to send only `facts` from the massing, so before a massing
 * existed it reported "no project data has been supplied" — while the app was
 * showing a traced 223.9 m2 plot on screen. Plot area alone selects the setback
 * band in SDBR Tables 19-23, so that answer was available all along.
 *
 * Declared values (the height limit typed into Build) are NOT achieved values.
 * They are sent because they choose which band applies, and are labelled so the
 * model cannot present one as a compliance result.
 */
function siteFactsFrom(project) {
  const sd = project?.siteData || {};
  const num = (v) => {
    if (v === '' || v == null) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const widths = Object.values(project?.roadEdgeWidths || {}).map(num).filter((n) => n != null);
  return {
    plot_area_m2: project?.areaM2 != null ? +Number(project.areaM2).toFixed(1) : null,
    road_width_m: num(project?.roadWidthM) ?? (widths.length ? Math.max(...widths) : null),
    // Documented defaults, so a bare site still gets an answer. Each is the
    // most common case for the market this serves, and each is declared as an
    // assumption in factTiers — never presented as something the user gave.
    building_height_m: num(sd.heightLimit) ?? DEFAULTS.building_height_m,
    zone: sd.zone || project?.zone || DEFAULTS.zone,
  };
}

/**
 * What to stand in when nothing has been supplied.
 *
 * Chosen to be the LEAST surprising case rather than the safest one, because a
 * default that quietly picks the strictest band gives an answer nobody asked
 * about. Each is surfaced as ASSUMED, with the control that replaces it, and
 * any verdict resting on one is downgraded to indicative server-side.
 *
 *   15 m  the threshold the SDBR setback tables split at — below it is the
 *         low-rise case, which is what a Tier-2/3 plot usually is
 *   residential  the most common zone, and the one the DCRs default to
 */
const DEFAULTS = {
  building_height_m: 15,
  zone: 'residential',
};

/**
 * Where each site-derived fact came from.
 *
 * Mirrors the tiers Site3DView emits once a massing exists, so the prompt gets
 * one vocabulary either way. The declared height limit is the interesting case:
 * it is a real user input, but it is what they INTEND to build rather than what
 * they have — so it can select the setback band honestly while never standing
 * in for an achieved height.
 */
function siteTiersFrom(project) {
  const sd = project?.siteData || {};
  const has = (v) => v !== '' && v != null;
  return {
    plot_area_m2: project?.areaM2
      ? { tier: 'measured', basis: 'traced site boundary' }
      : { tier: 'missing', basis: null },
    road_width_m: has(project?.roadWidthM)
      ? { tier: 'measured', basis: 'entered in the Build tab' }
      : { tier: 'missing', basis: null,
          changeIt: 'mark the road-facing edge, or enter the road width in the Build tab' },
    building_height_m: has(sd.heightLimit)
      ? { tier: 'measured', basis: 'Height Limit in the Build tab — intended, not built' }
      : { tier: 'assumed', basis: `${DEFAULTS.building_height_m} m — the height the setback tables split at`,
          changeIt: 'type the intended height into Height Limit in the Build tab' },
    zone: has(sd.zone) || project?.zone
      ? { tier: 'measured', basis: 'Land use zone in the Build tab' }
      : { tier: 'assumed', basis: `${DEFAULTS.zone} — the most common case`,
          changeIt: 'choose it in Land use zone in the Build tab' },
  };
}


/**
 * Each gap names the ACTION that closes it, so an answer can say "mark the
 * road-facing edge in the 3D view" instead of "no data supplied" — which tells
 * the user nothing about what to do next.
 *
 * A gap is only a gap if nothing is standing in for it. Zone and height carry
 * documented defaults (see DEFAULTS), so listing them here as well made the
 * assistant answer FROM the default and then demand the input anyway — five
 * asks-back in one answer, for facts it had already decided. Anything the tiers
 * have resolved to measured, derived or assumed drops out; its `changeIt` in
 * renderTiers already says how to replace it, and says it as a correction
 * rather than a prerequisite.
 */
function siteGapsFrom(project, hasMassing, tiers) {
  const sd = project?.siteData || {};
  const gaps = [];
  const roadEdges = (project?.roadEdgeIndices || []).length;
  if (!project?.areaM2) {
    gaps.push({ fact: 'plot_area_m2', missing: 'the site boundary',
                action: 'trace or sketch the plot in the map view' });
  }
  if (!roadEdges) {
    gaps.push({
      // Deliberately untagged. An OSM-derived road_width_m makes that fact
      // 'derived', but it cannot say WHICH edge the road is on — and the front
      // margin is measured from that edge. Tagging it here would let a width
      // scraped off a map suppress a gap the width does not fill.
      missing: 'which edge faces the road',
      action: 'press "Select site edge(s) towards road" in the Build tab, then click that edge in the 3D view',
      why: 'the front margin is measured from it, and the road width sets how deep it must be',
    });
  }
  if (!sd.heightLimit) {
    gaps.push({ fact: 'building_height_m',
                missing: 'the intended building height',
                action: 'type it into Height Limit in the Build tab',
                why: 'the setback tables split at 15 m' });
  }
  if (!sd.fsi) gaps.push({ missing: 'the permitted FSI', action: 'type it into FSI in the Build tab' });
  if (!sd.zone && !project?.zone) {
    gaps.push({
      fact: 'zone',
      missing: 'the land use zone',
      action: 'choose it in Land use zone in the Build tab',
      why: 'side and rear open spaces differ between a residential and an industrial plot',
    });
  }
  if (!hasMassing) {
    gaps.push({ missing: 'a built massing',
                action: 'press Build Up in the Build tab',
                why: 'nothing can be CHECKED against the rules until there is something to check' });
  }
  // Untagged gaps (FSI, the massing) have no tier to consult, so they stand.
  return gaps.filter((g) => {
    const t = g.fact && tiers?.[g.fact];
    return !t || t.tier === 'missing';
  });
}


export default function AnalysisAssistant({ project, apiBase, activeTab, facts: factsProp, achieved: achievedProp, collapsedDefault }) {
  const [open, setOpen] = useState(!collapsedDefault);
  const [focused, setFocused] = useState(false);

  // The massing's numbers, taken from the same event the compliance panel reads.
  //
  // They used to arrive only as props from the analysis drawer, which meant the
  // assistant could not exist outside it. It now sits under the 3D view and has
  // to answer before that drawer has ever been opened, so it subscribes for
  // itself. A caller that still passes them wins — the drawer knows which project
  // its numbers belong to, and this does not.
  const [ownInputs, setOwnInputs] = useState(null);
  // The conversation being appended to. Null until the first answer returns —
  // the server mints the id, so a chat only exists once it holds something.
  const [chatId, setChatId] = useState(null);
  // null = the list is closed, [] = open and empty. Named for what it holds:
  // `history` is a browser global and shadowing it is a lint error for good
  // reason — nobody should have to guess whether history.back() means the chat.
  const [pastChats, setPastChats] = useState(null);
  const [loadingHist, setLoadingHist] = useState(false);
  useEffect(() => {
    const onInputs = (e) => setOwnInputs(e.detail || null);
    window.addEventListener('complianceInputsChanged', onInputs);
    return () => window.removeEventListener('complianceInputsChanged', onInputs);
  }, []);
  const facts = factsProp !== undefined ? factsProp : ownInputs?.facts;
  const achieved = achievedProp !== undefined ? achievedProp : ownInputs?.achieved;
  const [turns, setTurns] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);

  /**
   * A conversation belongs to ONE project.
   *
   * Every answer is grounded in a specific site's area, road edges, massing and
   * room schedule, so a transcript left on screen after the project changed reads
   * as if it were about the new one. It is not, and nothing on it says so.
   *
   * The in-flight guard matters as much as the reset: a question asked about A
   * and answered after the user has switched to B would otherwise land in B's
   * transcript, which is worse than losing it.
   */
  const askedFor = useRef(null);
  useEffect(() => {
    askedFor.current = project?.id ?? null;
    setTurns([]);
    setInput('');
    setBusy(false);
    setOwnInputs(null);   // the previous project's massing numbers
    setChatId(null);      // a new project starts a new conversation
    setPastChats(null);
  }, [project?.id]);
  /**
   * Publish this strip's height, so what it floats over can step around it.
   *
   * The strip overlays the bottom of the 3D view, and the view's own message
   * stack sits at the same edge — so warnings landed underneath it. A fixed
   * offset in the stylesheet would only ever clear ONE state, and this thing has
   * several: one line collapsed, several hundred pixels with a conversation
   * open. A ResizeObserver reports the real height on every change.
   *
   * Set on the document element rather than a parent because there is only ever
   * one of these, and the reader of the value (site3d-messages) is in a
   * different subtree.
   */
  const stripRef = useRef(null);
  useEffect(() => {
    const el = stripRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const publish = () => {
      document.documentElement.style.setProperty('--assistant-h', `${Math.round(el.offsetHeight)}px`);
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => {
      ro.disconnect();
      // Leave nothing behind: a stale height would push the messages off a view
      // that no longer has a strip under them.
      document.documentElement.style.removeProperty('--assistant-h');
    };
  }, []);

  const scrollRef = useRef(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns, busy, open]);

  /** Open the pastChats list, fetching it fresh — another session may have added to it. */
  const openHistory = async () => {
    if (pastChats) { setPastChats(null); return; }   // second press closes it
    setLoadingHist(true);
    try {
      const res = await fetch(`${apiBase}/api/analysis/chats?projectId=${encodeURIComponent(project?.id ?? '')}`);
      const d = await res.json();
      setPastChats(d.ok ? d.chats : []);
    } catch (e) {
      setPastChats([]);
    } finally {
      setLoadingHist(false);
    }
  };

  /**
   * Load a past conversation into the panel.
   *
   * chatId is set to the loaded chat, so a follow-up question CONTINUES it rather
   * than forking a new one — reopening a thread and asking again is the reason to
   * have pastChats at all.
   */
  const loadChat = async (id) => {
    setLoadingHist(true);
    try {
      const res = await fetch(`${apiBase}/api/analysis/chats/${id}`);
      const d = await res.json();
      if (d.ok) {
        setTurns(d.turns || []);
        setChatId(id);
        setOpen(true);
        setPastChats(null);
      }
    } catch (e) { /* leave the panel as it was */ }
    finally { setLoadingHist(false); }
  };

  /** Start a fresh conversation without touching what is stored. */
  const newChat = () => {
    setTurns([]);
    setChatId(null);
    setPastChats(null);
  };


  const ask = async (question) => {
    const q = String(question || '').trim();
    if (!q || busy) return;
    const asked = project?.id ?? null;   // which project this answer belongs to
    setInput('');
    setOpen(true);
    setTurns((t) => [...t, { role: 'user', text: q }]);
    setBusy(true);
    // Tiers from the massing win where one has been built — those are measured off
    // real geometry; the site's own tiers fill the rest. Computed here because the
    // gap list is filtered against them below.
    const tiers = { ...siteTiersFrom(project), ...(ownInputs?.factTiers || {}) };
    try {
      const res = await fetch(`${apiBase}/api/analysis/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: q,
          activeTab,
          lat: project?.centerLat,
          lng: project?.centerLng,
          // this project's own numbers — lets the compliance path resolve the
          // governing value for THIS site instead of listing every table
          // Massing facts win; the site fills the gaps. Before a massing exists
          // the plot area alone already picks the setback band, so a question
          // asked on a freshly traced site is answerable rather than refused.
          facts: { ...siteFactsFrom(project), ...(facts || {}) },
          siteGaps: siteGapsFrom(project, !!achieved, tiers),
          achieved,
          climateContext: readClimateContext(facts),
          // The room schedule, the scale it was measured at, and the traced
          // massing's totals — read at ask time, so the answer matches what is on
          // screen rather than whatever was there when the panel mounted.
          designContext: readDesignContext(project?.id ?? null),
          // Storage keys. projectId is what pastChats is scoped by; chatId keeps
          // follow-up questions in one conversation instead of starting a new one
          // on every turn.
          // Tiers from the massing win where one has been built — those are measured
          // off real geometry; the site's own tiers fill the rest. Built above the
          // payload because siteGaps is filtered against exactly these — the two
          // must not disagree about whether a fact is still outstanding.
          factTiers: tiers,
          projectId: project?.id ?? null,
          chatId,
        }),
      });
      // Read the body as TEXT first, then parse.
      //
      // res.json() on an empty or non-JSON body throws "Failed to execute 'json'
      // on 'Response': Unexpected end of JSON input", and that string went
      // straight into the transcript as though it were the answer. It is a
      // browser internal that names no cause and no fix, and the cause is almost
      // always the simplest possible one: the backend is not running, so the dev
      // server proxied an empty body back. Say that instead.
      const raw = await res.text();
      let data;
      try {
        data = JSON.parse(raw);
      } catch (parseErr) {
        data = {
          ok: false,
          error: raw.trim()
            ? `The server replied with something that is not an answer (HTTP ${res.status}).`
            : 'No reply from the VAUOM server — it is probably not running. Start the backend and ask again.',
          detail: raw.slice(0, 300) || null,
        };
      }
      // Dropped, not appended, if the project changed while this was in flight.
      if (askedFor.current !== asked) return;
      if (data?.chatId) setChatId(data.chatId);
      setTurns((t) => [...t, { role: 'bot', data }]);
    } catch (e) {
      if (askedFor.current !== asked) return;
      // A thrown fetch means the request never completed at all — the server is
      // unreachable, or the tab lost the network. TypeError is what the browser
      // raises for both, and "Failed to fetch" tells the user nothing.
      const unreachable = e instanceof TypeError;
      setTurns((t) => [...t, { role: 'bot', data: {
        ok: false,
        error: unreachable
          ? 'Could not reach the VAUOM server. Check it is running, then ask again.'
          : e.message,
        detail: unreachable ? e.message : null,
      } }]);
    } finally {
      if (askedFor.current === asked) setBusy(false);
    }
  };

  return (
    <div className="assistant-strip" ref={stripRef}
         style={{ ...styles.wrap, height: open ? undefined : 'auto' }}>
      {/* A ROW, not a button. The header used to be one big button, so a second
          control could not live inside it — nesting a button in a button is invalid
          and the inner click would be swallowed. The title still toggles; History
          and New sit beside it. */}
      <div style={styles.bar}>
        <button type="button" style={styles.barToggle} onClick={() => setOpen((o) => !o)}>
          <span style={styles.caret}>{open ? '▾' : '▸'}</span>
          <span style={styles.barMark}><Sparkle size={13} /></span>
          <span style={styles.barTitle}>Assistant</span>
          <span style={styles.barSub}>
            {activeTab ? `asks about ${DOMAIN_LABEL[activeTab]?.split(' · ')[0] || activeTab}` : 'grounded in your project data'}
          </span>
          {turns.length > 0 && <span style={styles.count}>{turns.filter((t) => t.role === 'user').length}</span>}
        </button>

        {turns.length > 0 && (
          <button type="button" style={styles.barAction} onClick={newChat}
                  title="Start a new conversation. The current one stays in History.">
            New
          </button>
        )}
        <button type="button"
                style={{ ...styles.barAction, ...(pastChats ? styles.barActionOn : null) }}
                onClick={openHistory}
                aria-expanded={!!pastChats}
                title="Past conversations about this project">
          {loadingHist ? '…' : 'History'}
        </button>
      </div>

      {pastChats && (
        <div style={styles.histWrap}>
          {pastChats.length === 0 && (
            <div style={styles.histEmpty}>No past conversations for this project yet.</div>
          )}
          {pastChats.map((c) => (
            <button key={c.id} type="button" style={styles.histRow} onClick={() => loadChat(c.id)}>
              <span style={styles.histTitle}>{c.title}</span>
              <span style={styles.histMeta}>
                {c.questions} question{c.questions === 1 ? '' : 's'} · {new Date(c.updated_at).toLocaleDateString()}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* The ASK BOX is always mounted; only the conversation collapses.
          It was inside this guard, so the strip under the 3D view — which
          starts collapsed on purpose, to cost one line — showed a header with
          no way to type into it. The box is the whole point of the strip. */}
      {open && (
        <>
          {turns.length > 0 && (
            <div ref={scrollRef} style={styles.scroll}>
              {turns.map((t, i) =>
                t.role === 'user' ? (
                  <div key={i} style={styles.userRow}>
                    <span style={styles.userBubble}>{t.text}</span>
                  </div>
                ) : (
                  <BotTurn key={i} data={t.data} />
                )
              )}
              {busy && <div style={styles.thinking}>Looking it up…</div>}
            </div>
          )}
          {turns.length === 0 && busy && <div style={styles.thinking}>Looking it up…</div>}
        </>
      )}

          <form style={styles.composer} onSubmit={(e) => { e.preventDefault(); ask(input); }}>
            {/* The field is wrapped so the mark can sit INSIDE it. A separate icon
                beside the box would read as another control; inside, it labels the
                box as the place you ask. */}
            <div
              className={!input && !focused && !busy ? 'decon-ask-idle' : undefined}
              style={{ ...styles.field, ...(focused ? styles.fieldOn : null) }}
            >
              <span style={styles.fieldMark}><Sparkle size={13} /></span>
              <input
                style={styles.input}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                placeholder={placeholderFor(activeTab)}
                disabled={busy}
                aria-label="Ask the assistant about this analysis"
              />
            </div>
            <button
              type="submit"
              style={{ ...styles.send, ...(busy || !input.trim() ? styles.sendOff : null) }}
              disabled={busy || !input.trim()}
            >
              <Sparkle size={13} />
              {busy ? 'Asking…' : 'Ask'}
            </button>
          </form>
    </div>
  );
}

/**
 * Four-point sparkle — the established "ask a model" mark.
 *
 * Deliberately the same family as the AI Extraction button's purple: both are
 * model-backed features, and a user who has learned that purple means "this
 * asks an AI" should not have to learn it twice.
 */
function Sparkle({ size = 14, style }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor"
         aria-hidden="true" focusable="false" style={{ display: 'block', ...style }}>
      <path d="M12 2.6c.5 3.9 1.9 5.8 5.8 6.4-3.9.6-5.3 2.5-5.8 6.4-.5-3.9-1.9-5.8-5.8-6.4 3.9-.6 5.3-2.5 5.8-6.4Z" />
      <path d="M18.6 14.4c.28 2.1 1.05 3.1 3.15 3.45-2.1.35-2.87 1.35-3.15 3.45-.28-2.1-1.05-3.1-3.15-3.45 2.1-.35 2.87-1.35 3.15-3.45Z" />
    </svg>
  );
}


function placeholderFor(tab) {
  if (tab === 'climate') return 'Ask about sun, wind, cooling load, ventilation…';
  if (tab === 'water') return 'Ask about water demand, rainwater…';
  if (tab === 'electricity') return 'Ask about load, PV…';
  return 'Ask about setbacks, FSI, staircases, parking…';
}

/**
 * Render an answer with its important parts picked out.
 *
 * The model writes Markdown-ish prose, and it was being dropped into a pre-wrap
 * div — so "**FAILS**" arrived on screen with its asterisks, and a verdict, a
 * measurement and a clause id all looked exactly like the words around them.
 *
 * Presentation only. Nothing here changes a value, reorders a sentence, or adds
 * meaning: a number is emphasised because it is a number, not because it was
 * checked. React elements rather than dangerouslySetInnerHTML, because this text
 * comes from a language model and should never be able to inject markup.
 */
const INLINE = new RegExp(
  [
    '\\*\\*(?<bold>[^*]+)\\*\\*',                          // **bold**
    '\\[clause (?<clause>[^\\]]+)\\]',                     // [clause sdbr.t19.iv]
    '\\[(?<cites>\\d+(?:,\\s*\\d+)*)\\]',                  // [6] or [2, 3]
    '(?<verdict>\\bPASS(?:ES|ED)?\\b|\\bFAIL(?:S|ED)?\\b)',
    // a measurement: a number followed by a unit this app actually deals in
    // \b after the unit fails on "%": a percent sign is not a word character,
    // so "24.2%," had no boundary to match and the value was missed. A negative
    // lookahead for a letter or digit is the right test for "the unit ended".
    '(?<value>\\d[\\d,]*(?:\\.\\d+)?\\s?(?:m²|m2|sq ?ft|ft²|mm|%|m)(?![\\w²]))',
  ].join('|'),
  'g',
);

function inlineNodes(text, keyBase) {
  const out = [];
  let last = 0;
  let m;
  INLINE.lastIndex = 0;
  while ((m = INLINE.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const g = m.groups;
    const k = `${keyBase}-${m.index}`;
    if (g.bold) {
      // The model writes its verdict as **FAILS**, so the bold branch would win
      // and the chip would never appear. A bold run that IS a verdict gets the
      // chip; everything else stays bold.
      out.push(
        /^(PASS(ES|ED)?|FAIL(S|ED)?)$/i.test(g.bold.trim())
          ? (
            <span key={k} style={{ ...styles.verdict,
              ...(/^PASS/i.test(g.bold.trim()) ? styles.verdictPass : styles.verdictFail) }}>
              {g.bold.trim()}
            </span>
          )
          : <strong key={k} style={styles.mdBold}>{g.bold}</strong>,
      );
    } else if (g.clause) {
      out.push(<span key={k} style={styles.clauseChip} title="Clause in the source regulation">{g.clause}</span>);
    } else if (g.cites) {
      out.push(<sup key={k} style={styles.citeMark}>{g.cites}</sup>);
    } else if (g.verdict) {
      const pass = /^PASS/i.test(g.verdict);
      out.push(
        <span key={k} style={{ ...styles.verdict, ...(pass ? styles.verdictPass : styles.verdictFail) }}>
          {g.verdict}
        </span>,
      );
    } else if (g.value) {
      out.push(<span key={k} style={styles.value}>{g.value}</span>);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function RichAnswer({ text }) {
  const lines = String(text || '').split('\n');
  const blocks = [];
  let bullets = null;
  const flush = () => {
    if (bullets && bullets.length) {
      blocks.push(<ul key={`u${blocks.length}`} style={styles.mdList}>{bullets}</ul>);
    }
    bullets = null;
  };
  lines.forEach((raw, i) => {
    const line = raw.trimEnd();
    const bullet = line.match(/^\s*[*-]\s+(.*)$/);
    if (bullet) {
      bullets = bullets || [];
      bullets.push(<li key={`l${i}`} style={styles.mdItem}>{inlineNodes(bullet[1], i)}</li>);
      return;
    }
    flush();
    if (!line.trim()) return;
    // Headings. The prompts have always been free to emit them, and a
    // multi-domain answer now emits one per section — without this branch the
    // hashes printed literally as body text.
    const head = line.match(/^\s*(#{1,4})\s+(.*)$/);
    if (head) {
      blocks.push(
        <div key={`h${i}`} style={head[1].length > 2 ? styles.mdH3
                   : blocks.length ? styles.mdH2 : styles.mdH2First}>
          {inlineNodes(head[2], i)}
        </div>,
      );
      return;
    }
    blocks.push(<p key={`p${i}`} style={styles.mdPara}>{inlineNodes(line, i)}</p>);
  });
  flush();
  return <div style={styles.answer}>{blocks}</div>;
}


function BotTurn({ data }) {
  const [openCite, setOpenCite] = useState(null);
  const salvageable = data && data.ok === false && (data.passages || []).length > 0;

  if (!data || (data.ok === false && !salvageable)) {
    return (
      <div style={styles.botRow}>
        <div style={{ ...styles.notice, color: T.err, border: `1px solid ${T.err}` }}>
          {(data?.warnings && data.warnings[0]) || data?.error || 'Request failed.'}
        </div>
      </div>
    );
  }

  const { answer, citations = [], passages = [], warnings = [], stack = [], jurisdiction, domain, grounding } = data;

  return (
    <div style={styles.botRow}>
      <div style={styles.meta}>
        {domain && <span style={styles.domainChip}>{DOMAIN_LABEL[domain] || domain}</span>}
        {jurisdiction?.state && (
          <span style={{ color: T.textDim }}>
            {[jurisdiction.city, jurisdiction.state].filter(Boolean).join(', ')}
            {stack.length > 0 && ` · ${stack.join(' › ')}`}
          </span>
        )}
        {grounding === 'computed' && <span style={{ color: T.textDim }}>· from your project's computed results</span>}
      </div>

      {warnings.map((w, i) => (
        <div key={i} style={{ ...styles.notice, color: T.warn, border: `1px solid ${T.warn}` }}>{w}</div>
      ))}

      {answer && <RichAnswer text={answer} />}

      {!answer && passages.length > 0 && (
        <div style={styles.rawWrap}>
          <div style={styles.rawHead}>Source passages (no written answer):</div>
          {passages.map((p, i) => (
            <div key={i} style={styles.citeOpen}>
              <div style={styles.citeHead}>{p.code} {p.version} · p.{p.page}</div>
              <div style={styles.citeBody}>{p.body}</div>
            </div>
          ))}
        </div>
      )}

      {citations.length > 0 && (
        <div style={styles.citeWrap}>
          {citations.map((c, i) => (
            <button
              key={i}
              type="button"
              style={{
                ...styles.citeChip,
                border: `1px solid ${openCite === i ? T.accent : T.border}`,
                color: openCite === i ? T.accent : T.textDim,
              }}
              onClick={() => setOpenCite(openCite === i ? null : i)}
              title="Show the source excerpt"
            >
              {c.n ? `[${c.n}] ` : ''}{c.code} p.{c.page}
            </button>
          ))}
        </div>
      )}

      {openCite != null && citations[openCite]?.excerpt && (
        <div style={styles.citeOpen}>
          <div style={styles.citeHead}>
            {citations[openCite].code} {citations[openCite].version} · page {citations[openCite].page}
          </div>
          <div style={styles.citeBody}>{citations[openCite].excerpt}…</div>
        </div>
      )}

      {data.disclaimer && <div style={styles.disclaimer}>{data.disclaimer}</div>}
    </div>
  );
}

const styles = {
  // No border here: it floats now, and the four-sided border, radius and shadow
  // come from `.split-top > .assistant-strip` in main.css. A borderTop set inline
  // would have won over that rule on the top edge alone and drawn a seam.
  wrap: { flexShrink: 0, background: 'var(--surface-2)', display: 'flex', flexDirection: 'column', maxHeight: '55%', minHeight: 0 },
  bar: { display: 'flex', alignItems: 'center', gap: 7, padding: '4px 10px 4px 12px', flexShrink: 0 },
  barToggle: { flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 7, padding: '2px 0', background: 'none', border: 'none', color: T.text, cursor: 'pointer', textAlign: 'left' },
  /* Quiet until used. These sit beside a title, not in a toolbar, so a filled
     button here would outweigh the thing it labels. */
  barAction: { fontSize: 10.5, fontWeight: 600, padding: '3px 9px', borderRadius: 7,
               cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
               border: `1px solid ${T.border}`, background: 'transparent', color: T.textDim },
  barActionOn: { borderColor: 'var(--ans-cite, #7a44b8)', color: 'var(--ans-cite, #7a44b8)',
                 background: 'color-mix(in srgb, var(--ans-cite) 12%, transparent)' },
  histWrap: { maxHeight: 180, overflowY: 'auto', display: 'flex', flexDirection: 'column',
              gap: 2, padding: '2px 10px 8px', flexShrink: 0 },
  histEmpty: { fontSize: 11, color: T.textDim, padding: '6px 2px' },
  histRow: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1,
             textAlign: 'left', width: '100%', cursor: 'pointer', padding: '6px 8px',
             borderRadius: 7, border: `1px solid ${T.border}`, background: T.surface },
  histTitle: { fontSize: 11.5, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis',
               whiteSpace: 'nowrap', maxWidth: '100%' },
  histMeta: { fontSize: 9.5, color: T.textDim },
  caret: { color: T.textDim, width: 10, fontSize: 11 },
  barMark: { color: 'var(--ai-ink, #9b59d0)', display: 'flex' },
  barTitle: { fontSize: 12, fontWeight: 600 },
  barSub: { fontSize: 10, color: T.textDim },
  count: { marginLeft: 'auto', fontSize: 10, color: T.textDim, border: `1px solid ${T.border}`, borderRadius: 8, padding: '0 6px' },
  scroll: { flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 9, padding: '4px 12px 8px' },
  userRow: { display: 'flex', justifyContent: 'flex-end' },
  userBubble: { fontSize: 12, background: T.surfaceHi, border: `1px solid ${T.border}`, borderRadius: '10px 10px 2px 10px', padding: '5px 9px', maxWidth: '85%' },
  botRow: { display: 'flex', flexDirection: 'column', gap: 5, maxWidth: '95%' },
  meta: { fontSize: 9.5, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' },
  domainChip: { border: `1px solid ${T.border}`, borderRadius: 8, padding: '0 6px', color: T.accent, fontWeight: 600 },
  /* pre-wrap is gone: paragraphs and lists are real elements now, so the blank
     lines the model writes become spacing instead of empty rows. */
  answer: { fontSize: 12.5, lineHeight: 1.55, color: T.text, background: T.surface, border: `1px solid ${T.border}`, borderRadius: '10px 10px 10px 2px', padding: '9px 11px' },
  mdPara: { margin: '0 0 7px' },
  // A section heading in a multi-domain answer: the reader is scanning for
  // "which part answers my water question", so the rule above it does more work
  // than size would. First-child margin is trimmed so a heading can open a turn.
  mdH2: { margin: '12px 0 6px', paddingTop: 8, borderTop: `1px solid ${T.border}`,
          fontSize: 12, fontWeight: 700, letterSpacing: 0.3, color: T.text },
  mdH2First: { margin: '0 0 6px', fontSize: 12, fontWeight: 700, letterSpacing: 0.3, color: T.text },
  mdH3: { margin: '9px 0 5px', fontSize: 12, fontWeight: 700, color: T.text },
  mdList: { margin: '0 0 7px', paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 4 },
  mdItem: { margin: 0 },
  mdBold: { fontWeight: 700, color: T.text },
  /* A measurement is the thing an architect scans for, so it gets weight and a
     tabular figure — not a colour, which would compete with the verdicts. */
  value: { fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: 'var(--ans-value, #5da5e5)' },
  /* PASS and FAIL carry colour AND the word. Colour alone would fail a red-green
     colour-blind reader, and this is the one line they will look for. */
  verdict: { fontWeight: 800, fontSize: 11, letterSpacing: 0.3, padding: '1px 7px', borderRadius: 999, whiteSpace: 'nowrap' },
  verdictPass: { background: 'color-mix(in srgb, var(--ans-pass) 16%, transparent)', color: 'var(--ans-pass)', border: '1px solid color-mix(in srgb, var(--ans-pass) 45%, transparent)' },
  verdictFail: { background: 'color-mix(in srgb, var(--ans-fail) 15%, transparent)', color: 'var(--ans-fail)', border: '1px solid color-mix(in srgb, var(--ans-fail) 45%, transparent)' },
  clauseChip: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 10.5, padding: '1px 5px', borderRadius: 4, background: T.surfaceHi, border: `1px solid ${T.border}`, color: T.textDim, whiteSpace: 'nowrap' },
  citeMark: { fontSize: 9, fontWeight: 700, color: 'var(--ans-cite, #9b59d0)', padding: '0 1px' },
  notice: { fontSize: 11, border: '1px solid', borderRadius: 6, padding: '6px 8px', background: 'color-mix(in srgb, var(--warn) 10%, transparent)', lineHeight: 1.4 },
  citeWrap: { display: 'flex', flexWrap: 'wrap', gap: 5 },
  citeChip: { fontSize: 9.5, fontWeight: 600, border: '1px solid', borderRadius: 4, padding: '2px 6px', background: 'transparent', cursor: 'pointer', whiteSpace: 'nowrap' },
  citeOpen: { border: `1px solid ${T.border}`, borderRadius: 6, padding: 8, background: 'var(--surface-2)' },
  citeHead: { fontSize: 10, fontWeight: 600, color: T.accent, marginBottom: 3 },
  citeBody: { fontSize: 10.5, color: T.textDim, lineHeight: 1.45, whiteSpace: 'pre-wrap', maxHeight: 170, overflow: 'auto' },
  rawWrap: { display: 'flex', flexDirection: 'column', gap: 6 },
  rawHead: { fontSize: 11, color: T.textDim },
  disclaimer: { fontSize: 9.5, color: T.textDim, lineHeight: 1.4, fontStyle: 'italic' },
  thinking: { fontSize: 11, color: T.textDim, padding: '2px 12px 6px' },
  composer: { display: 'flex', gap: 6, padding: '0 12px 10px', flexShrink: 0 },
  /* The box, not the input, carries the border and the ring — so the mark
     inside it sits within the same outline the user reads as "the field". */
  /* --field, not --surface-2. The strip's own background IS --surface-2, so the
     box was exactly the colour of the thing it sat on and only a 1px border
     said it was there at all. These are the same tokens the Site Parameters and
     room-schedule inputs use, so every place you can type looks alike. */
  field: { flex: 1, display: 'flex', alignItems: 'center', gap: 7,
           padding: '0 10px', borderRadius: 9,
           background: 'var(--ask-field, #fff)',
           border: '1px solid var(--ask-field-border, #6f7d92)',
           transition: 'border-color .14s, box-shadow .14s' },
  fieldOn: { borderColor: 'var(--ai, #9b59d0)',
             boxShadow: '0 0 0 3px color-mix(in srgb, var(--ai) 22%, transparent)' },
  fieldMark: { color: 'var(--ans-cite, #7a44b8)', flexShrink: 0, display: 'flex' },
  /* --field-ink, NOT --text. In dark mode --field is near-white, so the normal
     light body colour would have written pale text onto a pale box. */
  input: { flex: 1, fontSize: 12, padding: '8px 0', border: 'none',
           background: 'transparent', color: 'var(--field-ink, #111827)',
           outline: 'none' },
  /* Filled, not outlined. Next to a full-width field an outlined button reads
     as part of the field's chrome; this is the one thing here that acts. */
  send: { display: 'inline-flex', alignItems: 'center', gap: 6,
          fontSize: 12, fontWeight: 600, padding: '8px 15px', borderRadius: 9,
          border: '1px solid var(--ai-btn, #7a44b8)',
          background: 'var(--ai-btn-grad, #7a44b8)', color: '#fff',
          cursor: 'pointer', flexShrink: 0,
          transition: 'filter .14s, box-shadow .14s' },
  /* Nothing to send yet. Flat and greyed rather than a dimmed gradient, which
     at low opacity loses the contrast its white label needs. */
  sendOff: { background: T.surfaceHi, border: `1px solid ${T.border}`,
             color: T.textDim, cursor: 'not-allowed', boxShadow: 'none' },
};
